import type { ColumnsType } from 'antd/es/table';
import { IcCopy, IcEdit, IcImage, IcScreen, IcTrash, Pagination, Table, Text } from 'basic-ui-lib';
import { ActionProps } from 'basic-ui-lib/components/Table/Table';
import dayjs from 'dayjs';
import { IPlaylist } from 'modules/playlist/interfaces/api';
import playlistApis from 'modules/playlist/services/apis';
import useGetListPlaylists from 'modules/playlist/services/hooks/useGetListPlaylists';
import { useNavigate } from 'react-router-dom';
import { getMessageError } from 'services/response';
import styled from 'styled-components';
import toast from 'utils/toast';
import ModalConfirm, { useModalConfirmRef } from 'widgets/ModalConfirm';
import { setPageLoading } from 'widgets/PageLoading';
import SetToScreenModal, { SetToScreenModalRef} from '../SetToScreenModal';
import { useRef } from 'react';
import EditNameModal, { EditNameModalRef } from 'basic-ui-lib/components/Modal/EditNameModal';

const PlaylistTable = () => {
  const [playlists, playlistsParam, mutate] = useGetListPlaylists();
  const editNameModalRef = useRef<EditNameModalRef>(null);
  
  const navigate = useNavigate();
  const modalConfirmRef = useModalConfirmRef();
  const setToScreenModalRef = useRef<SetToScreenModalRef>(null);

  const columns: ColumnsType<IPlaylist> = [
    {
      title: 'NAME',
      dataIndex: 'name',
      render: (text) => (
        <Text content_2 color='primary.6'>
          {text}
        </Text>
      ),
    },
    {
      title: 'DURATION',
      dataIndex: 'duration',
      render: (text) => (
        <Text body_1 medium>
          {dayjs()
            .startOf('day')
            .add(text || 0, 'second')
            .format('HH:mm:ss')}
        </Text>
      ),
    },
    {
      title: 'ASSIGNED DEVICES',
      dataIndex: 'numberAssigned',
    },
    {
      title: 'CREATED DATE',
      dataIndex: 'createdDate',
    },
    {
      title: 'LAST UPDATED',
      dataIndex: 'lastModifiedDate',
    },
  ];

  const actions: ActionProps<IPlaylist>[] = [
    {
      label: 'Set To Screen',
      icon: <IcScreen width={16} height={16} />,
      onClick: (item) => {
        setToScreenModalRef.current?.open(item.id);
      },
    },
    {
      label: 'Edit Name',
      icon: <IcEdit width={16} height={16} />,
      onClick: (item) => {
        editNameModalRef.current?.open({ currentName: item.name, id: item.id, onFinish: handleEditName });
      },
    },
    {
      label: 'Edit Content',
      icon: <IcImage width={16} height={16} />,
      onClick: (item) => {
        navigate(`/playlist/${item.id}`);
      },
    },
    {
      label: 'Duplicate',
      icon: <IcCopy width={16} height={16} />,
      onClick: (item) => {
        console.log('Duplicate clicked', item.id);
      },
    },
    {
      label: 'Delete',
      icon: <IcTrash width={16} height={16} />,
      onClick: (item) => {
        onDeletePlaylist(item.id);
      },
    }
  ];

  const handleDeletePlaylist = async (playlistId: string) => {
    try {
      setPageLoading(true);
      const res = await playlistApis.deletePlaylist(playlistId);
      if(res?.data?.data) {
        toast.success('Playlist deleted successfully');
        mutate({
          ...playlistsParam,
        });
      }
    } catch (error) {
      toast.error(getMessageError(error));
    }
    finally {
      setPageLoading(false);
    }
  };

  const onDeletePlaylist = (playlistId: string) => {
    modalConfirmRef.current?.open(() => handleDeletePlaylist(playlistId), {
      message: 'Are you sure you want to delete this playlist? This action cannot be undone.',
    });
  };

  const handlePageChange = (page: number) => {
    mutate({ ...playlistsParam, page });
  };

  const handleEditName = async (id: string, name: string) => {
    await playlistApis.updateNamePlaylist(id, name);
    mutate({ ...playlistsParam });
  };

  return (
    <TableBox>
      <Table<IPlaylist>
        className='custom-table'
        columns={columns}
        dataSource={playlists?.content || []}
        scroll={{ x: 'max-content' }}
        pagination={false}
        actions={actions}
      />
      <Pagination
        totalItems={playlists?.totalElements || 0}
        itemsPerPage={playlistsParam?.size || 10}
        currentPage={playlistsParam?.page || 1}
        onPageChange={handlePageChange}
        labelDisplay='playlists'
        showRowPerPage={true}
        maxVisiblePages={3}
      />
      <ModalConfirm ref={modalConfirmRef} />
      <SetToScreenModal ref={setToScreenModalRef} />
      <EditNameModal ref={editNameModalRef} />
    </TableBox>
  );
};

export default PlaylistTable;

const TableBox = styled.div`
  width: 100%;
`;
